import type { Invoice } from '@fpc/shared';
import { Money } from '@/components/ui';

/**
 * GST under reverse charge (RCM): one tick on the invoice. Ticked, the GST on
 * the invoice is the company's to pay to the government, not the vendor's to
 * collect. Set at review and confirmed at accounting; it is deducted from the
 * vendor's net payment.
 */
export function RcmCheckbox({
  checked,
  onChange,
  gst,
  currency,
  showAmount = true,
  compact = false,
}: {
  checked: boolean;
  onChange(checked: boolean): void;
  /** The GST on the invoice: under RCM, all of it is reversed. */
  gst: number;
  currency: string;
  /** Off where the amount is already shown beside the figures. */
  showAmount?: boolean;
  /** A short control for placing RCM inside the invoice Tax field. */
  compact?: boolean;
}) {
  if (compact) {
    return (
      <div className="mt-2 space-y-1">
        <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
          <input
            type="checkbox"
            className="h-4 w-4 accent-brand-600"
            aria-label="RCM (reverse charge)"
            checked={checked}
            onChange={(event) => onChange(event.target.checked)}
          />
          <span>RCM</span>
        </label>
        {checked ? (
          gst > 0 ? (
            <p className="text-xs text-amber-700">
              <Money currency={currency} minor={gst} className="font-semibold" /> RCM deducted from
              net payable
            </p>
          ) : (
            <p className="notice-warning text-xs">
              Enter a tax amount to calculate the RCM deduction.
            </p>
          )
        ) : null}
      </div>
    );
  }

  return (
    <div className="space-y-2 sm:col-span-2">
      <label
        className={`flex cursor-pointer items-start gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
          checked ? 'border-amber-300 bg-amber-50/70' : 'border-slate-200 hover:bg-slate-50'
        }`}
      >
        <input
          type="checkbox"
          className="mt-0.5 h-4 w-4 accent-brand-600"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
        />
        <span className="min-w-0">
          <span className="block text-sm font-medium text-slate-800">RCM (reverse charge)</span>
          <span className="block text-xs text-slate-500">
            The GST on this invoice is paid by the company, not the vendor.
          </span>
        </span>
      </label>
      {checked && showAmount ? (
        gst > 0 ? (
          <p className="text-sm">
            GST reversed under RCM:{' '}
            <Money currency={currency} minor={gst} className="font-semibold text-amber-700" />
            <span className="block text-xs text-slate-500">
              Payable by the company and deducted from the vendor payment.
            </span>
          </p>
        ) : (
          <p className="notice-warning text-xs">This invoice has no GST to reverse.</p>
        )
      ) : null}
    </div>
  );
}

/** RCM as recorded, read-only. */
export function RcmSummary({ invoice }: { invoice: Invoice }) {
  if (!invoice.rcmApplicable) {
    return <p className="text-sm text-slate-500">Not applicable</p>;
  }
  return (
    <p className="text-sm text-amber-700">
      <Money currency={invoice.currency} minor={invoice.rcmAmount} />
      <span className="text-xs text-slate-500"> RCM deducted from net payable</span>
    </p>
  );
}
